import React, { useState } from 'react';
import { APPS_DATA } from '../data/mockData';
import { KeywordItem } from '../types';
import { Search, Sparkles, ArrowUp, ArrowDown, Minus } from 'lucide-react';

interface KeywordsViewProps {
  onSelectApp: (appId: string) => void;
}

export const KeywordsView: React.FC<KeywordsViewProps> = ({ onSelectApp }) => {
  const [filter, setFilter] = useState<'all' | 'opportunities' | 'high_volume'>('all');
  const [search, setSearch] = useState('');

  // Flatten all keywords from all apps
  const allKeywordsWithApp: { keyword: KeywordItem; appName: string; appId: string }[] = [];
  Object.values(APPS_DATA).forEach((app) => {
    app.keywords.forEach((k) => {
      allKeywordsWithApp.push({ keyword: k, appName: app.name, appId: app.id });
    });
  });

  const filtered = allKeywordsWithApp.filter((item) => {
    if (search && !item.keyword.keyword.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === 'opportunities') return item.keyword.isOpportunity || item.keyword.movement > 0;
    if (filter === 'high_volume') return item.keyword.searchVolume >= 85;
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 border-b border-slate-200/80">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold mb-2">
            <Search className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>ASO & Search Volume Radar</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-950 tracking-tight">
            Keyword Intelligence Explorer
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Discover high-intent search queries, keyword movement velocity, and breakout organic opportunities.
          </p>
        </div>

        {/* Search Input */}
        <div className="w-full md:w-72">
          <input
            type="text"
            placeholder="Search keywords..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full text-xs bg-white border border-slate-200 rounded-2xl px-3.5 py-2.5 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          />
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 text-xs font-semibold">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-2xl transition-all border ${
            filter === 'all' ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-slate-700 border-slate-200'
          }`}
        >
          All Keywords ({allKeywordsWithApp.length})
        </button>
        <button
          onClick={() => setFilter('opportunities')}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl transition-all border ${
            filter === 'opportunities' ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-slate-700 border-slate-200'
          }`}
        >
          <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />
          <span>High Opportunity</span>
        </button>
        <button
          onClick={() => setFilter('high_volume')}
          className={`px-4 py-2 rounded-2xl transition-all border ${
            filter === 'high_volume' ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-slate-700 border-slate-200'
          }`}
        >
          High Search Volume (85+)
        </button>
      </div>

      {/* Keywords Table */}
      <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-100 text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
                <th className="pb-3 pl-2">Keyword Query</th>
                <th className="pb-3">Top Ranking App</th>
                <th className="pb-3 text-center">Rank</th>
                <th className="pb-3 text-center">Search Vol</th>
                <th className="pb-3 text-center">Difficulty</th>
                <th className="pb-3 text-right pr-2">Movement</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium">
              {filtered.map((item, idx) => {
                const k = item.keyword;
                const isUp = k.movement > 0;
                const isDown = k.movement < 0;

                return (
                  <tr key={`${k.keyword}-${idx}`} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-3.5 pl-2 font-bold text-slate-900 flex items-center gap-2">
                      <span>{k.keyword}</span>
                      {k.isOpportunity && (
                        <span className="text-[10px] bg-sky-100 text-sky-800 font-bold px-1.5 py-0.5 rounded">
                          Opportunity
                        </span>
                      )}
                    </td>

                    <td className="py-3.5">
                      <button
                        onClick={() => onSelectApp(item.appId)}
                        className="text-xs text-blue-600 hover:underline font-semibold"
                      >
                        {item.appName.split(':')[0]}
                      </button>
                    </td>

                    <td className="py-3.5 text-center">
                      <span className="font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded-md">
                        #{k.rank}
                      </span>
                    </td>

                    <td className="py-3.5 text-center">
                      <div className="inline-flex items-center gap-1.5 font-semibold text-slate-700">
                        <span>{k.searchVolume}</span>
                        <div className="w-12 bg-slate-100 h-1.5 rounded-full overflow-hidden hidden sm:block">
                          <div
                            className="bg-blue-500 h-full rounded-full"
                            style={{ width: `${k.searchVolume}%` }}
                          />
                        </div>
                      </div>
                    </td>

                    <td className="py-3.5 text-center">
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                          k.difficulty === 'High'
                            ? 'bg-rose-50 text-rose-700 border border-rose-100'
                            : k.difficulty === 'Medium'
                            ? 'bg-amber-50 text-amber-700 border border-amber-100'
                            : 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                        }`}
                      >
                        {k.difficulty}
                      </span>
                    </td>

                    <td className="py-3.5 text-right pr-2 font-bold">
                      {isUp && (
                        <span className="inline-flex items-center gap-0.5 text-emerald-600">
                          <ArrowUp className="w-3 h-3" strokeWidth={2.5} />
                          <span>+{k.movement}</span>
                        </span>
                      )}
                      {isDown && (
                        <span className="inline-flex items-center gap-0.5 text-rose-500">
                          <ArrowDown className="w-3 h-3" strokeWidth={2.5} />
                          <span>{Math.abs(k.movement)}</span>
                        </span>
                      )}
                      {k.movement === 0 && (
                        <span className="inline-flex items-center justify-end text-slate-400 font-normal">
                          <Minus className="w-3 h-3" strokeWidth={2} />
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
